import { Request, CreateRequestData, UpdateRequestData } from '../types';
import { api } from './api';
import { PreferencesService } from './preferencesService';

import type {
  AggregateSavePayload,
  AggregateSaveResult,
} from '../types/aggregateSave';

export interface PaginationParams {
  page: number;
  pageSize: number;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  searchText?: string;
  region?: string;
  stage?: string;
  modifiedOn?: string;
  modifiedBy?: string;
  /** #1575: 'unread' | 'read'. Omitted means the list is not filtered by read state. */
  readStatus?: string;
  userRoles?: string[]; // Add user roles for server-side filtering
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Service for managing requests via the API
 */
export class RequestsService {
  /**
   * Fetch all requests
   * Note: API always returns paginated format now, so we extract the data array
   */
  static async getAllRequests(): Promise<Request[]> {
    const response = await api.get<PaginatedResponse<Request>>('/requests');
    return response.data;
  }

  /**
   * Fetch requests with pagination
   */
  static async getRequestsPaginated(
    params: PaginationParams,
  ): Promise<PaginatedResponse<Request>> {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });

    if (params.orderBy) {
      queryParams.append('orderBy', params.orderBy);
    }

    if (params.orderDirection) {
      queryParams.append('orderDirection', params.orderDirection);
    }

    if (params.searchText) {
      queryParams.append('searchText', params.searchText);
    }

    if (params.region) {
      queryParams.append('region', params.region);
    }

    if (params.stage) {
      queryParams.append('stage', params.stage);
    }

    if (params.readStatus) {
      queryParams.append('readStatus', params.readStatus);
    }

    if (params.modifiedOn) {
      queryParams.append('modifiedOn', params.modifiedOn);
    }

    if (params.modifiedBy) {
      queryParams.append('modifiedBy', params.modifiedBy);
    }

    if (params.userRoles && params.userRoles.length > 0) {
      queryParams.append('userRoles', params.userRoles.join(','));
    }

    return api.get<PaginatedResponse<Request>>(`/requests?${queryParams}`);
  }

  /**
   * Fetch a request by ID
   */
  static async getRequestById(id: string): Promise<Request> {
    return api.get<Request>(`/requests/${id}`);
  }

  /**
   * Record that the caller has read this request (#1575, story 3.2).
   *
   * `readAt` is the instant the person OPENED the request, not the instant this
   * call is made — on a slow link those differ by seconds, and a change that
   * landed in that gap must not be marked seen. Omitting it makes the server
   * stamp its own `now` (`validateReadAt`), which is the documented escape for a
   * device whose clock cannot be trusted; see `recordRequestRead`.
   *
   * The endpoint answers 204 with no body, so there is nothing to resolve with.
   * Rejections are `ApiError` as usual — callers decide what a 4xx means.
   *
   * @see server/services/resource-read.service.ts
   */
  static async markRead(id: string, readAt?: string): Promise<void> {
    await api.post<void>(
      `/requests/${id}/read`,
      readAt === undefined ? {} : { readAt },
    );
  }

  /**
   * Create a new request
   */
  static async createRequest(
    data: CreateRequestData,
    options?: { deferVersionCapture?: boolean },
  ): Promise<Request> {
    // #1501 aggregate create: defer the create's version capture to the
    // follow-up POST /:id/save so the whole create is ONE history entry.
    const qs = options?.deferVersionCapture ? '?deferVersionCapture=1' : '';
    await PreferencesService.rememberDimensionUnitDefaultsLocally(
      (data as { dimensionUnits?: unknown }).dimensionUnits,
    );
    return api.post<Request>(`/requests${qs}`, data);
  }

  /**
   * Update a request (partial update)
   */
  static async updateRequest(
    id: string,
    data: UpdateRequestData,
    options?: { deferVersionCapture?: boolean },
  ): Promise<Request> {
    const qs = options?.deferVersionCapture ? '?deferVersionCapture=1' : '';
    await PreferencesService.rememberDimensionUnitDefaultsLocally(
      data.dimensionUnits,
    );
    return api.patch<Request>(`/requests/${id}${qs}`, data);
  }

  /**
   * Aggregate save (#1501 Option B): the whole of one user Save in one call —
   * one Version History entry by construction. 207 responses (partial) resolve
   * normally; callers inspect result.status/failed. While the server flag is
   * off this rejects with the 404 AGGREGATE_SAVE_DISABLED body — callers fall
   * back to the legacy per-section sequence.
   */
  static async saveAggregate(
    id: string,
    payload: AggregateSavePayload,
  ): Promise<AggregateSaveResult> {
    await PreferencesService.rememberDimensionUnitDefaultsLocally(
      payload.request?.dimensionUnits,
    );
    return api.post<AggregateSaveResult>(`/requests/${id}/save`, payload);
  }

  /**
   * Replace a request (full update)
   */
  static async replaceRequest(
    id: string,
    data: CreateRequestData,
  ): Promise<Request> {
    return api.put<Request>(`/requests/${id}`, data);
  }

  /**
   * Delete a request
   */
  static async deleteRequest(id: string): Promise<void> {
    return api.delete<void>(`/requests/${id}`);
  }

  /**
   * Close a Draft request without archiving it.
   *
   * The server writes CLOSED itself, so there is deliberately no body: a client cannot
   * name the target stage. Unlike deleteRequest this leaves is_archived alone, so the
   * request stays in the list under the Closed filter.
   */
  static async closeDraftRequest(id: string): Promise<Request> {
    return api.post<Request>(`/requests/${id}/close`, {});
  }

  /**
   * Reject an awaiting approval request with a persisted reason.
   */
  static async rejectRequest(id: string, reason: string): Promise<Request> {
    return api.post<Request>(`/requests/${id}/reject`, { reason });
  }

  /**
   * Submit a request (save + stage or approval transition).
   * `intent` is required by the server (#1456 Story 1.4).
   */
  static async submitRequest(
    id: string,
    data: UpdateRequestData,
    intent:
      | 'submit_for_approval'
      | 'submit_to_vertiv'
      | 'approve' = 'submit_to_vertiv',
  ): Promise<Request> {
    const dataWithUser = {
      ...data,
      intent,
      userEmail: data.userEmail || 'user@example.com',
    };
    return api.post<Request>(`/requests/${id}/submit`, dataWithUser);
  }

  /**
   * Create and submit a new request (creates in DRAFT stage)
   * This will create a new request and set its stage to DRAFT
   */
  static async createAndSubmitRequest(
    data: CreateRequestData,
  ): Promise<Request> {
    // Include userEmail from current user context (should be provided by auth)
    const dataWithUser = {
      ...data,
      userEmail: data.userEmail || 'user@example.com', // In production, get from auth context
    };
    return api.post<Request>('/requests/create-and-submit', dataWithUser);
  }
}

export default RequestsService;
